import { QueryKey } from '@/configs/queryKeys';
import { Client, Merchant } from '@/types/user';
import { BaseEntity, UUID } from '@/types/types';
import { Product } from './ProductApi';
import { CreatorOptions } from '@/types/creator';
import { fileTypeToHeader } from '@/utils/utilities';
import { ShippingDetails } from './OrderApi';
import EntityApi from './EntityApi';
import httpClient from './httpClient';

export interface OfferItem extends BaseEntity {
	offerId: UUID;
	product: Product;
	quantity: number;
	deliveryDate: string;
	comment: string;
}

export interface CustomOfferItem {
	name: string;
	taxRate: number;
	netPrice: number;
}

export enum DiscountType {
	FIXED = 'fixed',
	PERCENTAGE = 'percentage',
}

export enum ExportType {
	PDF = 'pdf',
	XLSX = 'xlsx',
}

export interface Offer extends BaseEntity, ShippingDetails {
	client: Client;
	merchant?: Merchant;
	number: number;
	totalPrice: number;
	subtotal: number;
	discount: number;
	discountType: DiscountType;
	offerItems: OfferItem[];
	customOfferItems: CustomOfferItem[];
}

class OfferApi extends EntityApi<Offer> {
	constructor() {
		super(QueryKey.offers);
		this.addProductToOffer = this.addProductToOffer.bind(this);
		this.generatePdf = this.generatePdf.bind(this);
		this.deleteOfferItem = this.deleteOfferItem.bind(this);
		this.updateOfferItem = this.updateOfferItem.bind(this);
	}

	async addProductToOffer({
		offerId,
		product,
		options,
		quantity,
	}: {
		offerId: UUID;
		product: Product;
		options: CreatorOptions;
		quantity: number;
	}) {
		const response = await httpClient.post<Offer>(
			`${this.queryKey}/${offerId}/add-to-offer`,
			{
				product: {
					id: product.id,
					productType: { id: product.productType.id },
				},
				quantity,
			}
		);

		return response.data;
	}

	async getOfferItem(offerId: UUID) {
		const response = await httpClient.get<OfferItem>(
			`${QueryKey.offerItems}/${offerId}`
		);
		return response.data;
	}

	async deleteOfferItem({
		offerId,
		offerItemId,
	}: {
		offerId: UUID;
		offerItemId: UUID;
	}) {
		await httpClient.delete(`${this.queryKey}/${offerId}/items/${offerItemId}`);
		return 1;
	}

	async updateOfferItem({
		offerId,
		payload,
	}: {
		offerId: UUID;
		payload: Pick<OfferItem, 'id' | 'quantity'>;
	}) {
		const response = await httpClient.patch<Offer>(
			`${this.queryKey}/${offerId}/items/${payload.id}`,
			payload
		);
		return response.data;
	}

	async generatePdf({
		offer,
		exportType,
	}: {
		offer: Offer;
		exportType: ExportType;
	}) {
		const response = await httpClient.get<Blob>(
			`${this.queryKey}/${offer.id}/export/${exportType}`,
			{
				headers: {
					Accept: fileTypeToHeader('pdf'),
				},
				responseType: 'blob',
			}
		);

		return response.data;
	}
}

export default new OfferApi();
